import { Link, Outlet, redirect, RouteObject, useMatch } from "react-router";
import React from "react";
import {
  ApiOutlined,
  ClusterOutlined,
  GoldOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Trans } from "@lingui/react/macro";
import { Menu } from "antd";

import { convertRouteModule } from "../../lib/routing";
import { useFeature } from "../../lib/contexts/feature-flags";
import { addIf } from "../../lib/util";
import { Topbar } from "../../lib/ui-components/layout/titlebar";
import { OrganizationSwitcher } from "../../lib/ui-components/layout/organization-select";

export function SettingsLayout({ children }: React.PropsWithChildren) {
  const canManageClients = useFeature("permissions/client-management");
  const canManageOrganization = useFeature("permissions/organization-management");

  const selectedKey = useMatch(":orgaId/settings/:setting")?.params.setting ?? "";

  const menuItems = [
    {
      icon: <SettingOutlined />,
      key: "general",
      label: <Link to={"general"}>{<Trans>General</Trans>}</Link>,
    },
    {
      icon: <TeamOutlined />,
      key: "members",
      label: <Link to={"members"}>{<Trans>Members</Trans>}</Link>,
    },
    {
      icon: <GoldOutlined />,
      key: "media",
      label: <Link to={"media"}>{<Trans>Media</Trans>}</Link>,
    },
    ...addIf(canManageClients, [
      {
        icon: <ApiOutlined />,
        key: "api-clients",
        label: <Link to={"api-clients"}>{<Trans>API Clients</Trans>}</Link>,
      },
    ]),
    ...addIf(canManageOrganization, [
      {
        icon: <ClusterOutlined />,
        key: "edge-devices",
        label: <Link to={"edge-devices"}>{<Trans>Edge Devices</Trans>}</Link>,
      },
    ]),
  ];

  return (
    <div className="grid h-dvh grid-cols-[auto_1fr] grid-rows-[auto_1fr]">
      <div className="col-span-full">
        <Topbar>
          <OrganizationSwitcher />
        </Topbar>
      </div>
      <nav className="bg-white overflow-y-auto h-full w-[var(--sidebar-width)]">
        <Menu mode="inline" items={menuItems} selectedKeys={[selectedKey]} />
      </nav>
      <div className="overflow-y-auto h-full">{children ?? <Outlet />}</div>
    </div>
  );
}

export const settingsRoutes: Array<RouteObject> = [
  {
    element: <SettingsLayout />,
    children: [
      { index: true, loader: () => redirect("general") },
      {
        path: "general",
        lazy: async () => convertRouteModule(await import("./general")),
      },
      {
        path: "members",
        lazy: async () => convertRouteModule(await import("./members")),
      },
      {
        path: "media",
        lazy: async () => convertRouteModule(await import("./media")),
      },
      {
        path: "api-clients",
        lazy: async () => convertRouteModule(await import("./clients")),
      },
      {
        path: "edge-devices",
        lazy: async () => convertRouteModule(await import("./edge-devices")),
      },
    ],
  },
];
